<script>
export default {
	name: 'ProfilePointsCard',
	props: {
		points: {
			type: Number,
			default: 0
		},
		target: {
			type: Number,
			default: 0
		}
	},
	computed: {
		normalizedPoints() {
			const value = Number(this.points)
			return Number.isFinite(value) ? Math.max(0, value) : 0
		},
		normalizedTarget() {
			const value = Number(this.target)
			return Number.isFinite(value) ? Math.max(0, value) : 0
		},
		remainingPoints() {
			return Math.max(0, this.normalizedTarget - this.normalizedPoints)
		},
		numberLocale() {
			const locale = String(this.$i18n?.locale || 'pt').toLowerCase()
			if (locale.startsWith('pt')) return 'pt-BR'
			if (locale.startsWith('en')) return 'en-US'
			return 'es'
		},
		formattedPoints() {
			return new Intl.NumberFormat(this.numberLocale).format(this.normalizedPoints)
		},
		formattedTarget() {
			return new Intl.NumberFormat(this.numberLocale).format(this.normalizedTarget)
		},
		formattedRemainingPoints() {
			return new Intl.NumberFormat(this.numberLocale).format(this.remainingPoints)
		},
		progress() {
			if (!this.normalizedTarget) return 0
			return Math.min(100, Math.round((this.normalizedPoints / this.normalizedTarget) * 100))
		}
	}
}
</script>

<template>
	<article class="profile-info-card profile-points-card" data-qa="profile-points-card">
		<div class="profile-points-card__labels">
			<div class="profile-points-card__current">
				<h2>{{ $t('myArea.headerInfo.pointsTitle') }}</h2>
				<p><strong data-qa="profile-points-current-text">{{ formattedPoints }}</strong> {{ $t('myArea.headerInfo.pointsLabel') }}</p>
			</div>
			<p class="profile-points-card__target" data-qa="profile-points-target-text">
				{{ $t('myArea.headerInfo.nextLevel', { target: formattedTarget }) }}
			</p>
		</div>
		<div
			class="profile-points-card__progress"
			data-qa="profile-points-progress"
			role="progressbar"
			aria-valuemin="0"
			:aria-valuemax="normalizedTarget"
			:aria-valuenow="normalizedPoints"
			:aria-label="$t('myArea.headerInfo.pointsProgressAriaLabel')"
		>
			<span :style="{ width: `${progress}%` }"></span>
		</div>
		<p class="profile-points-card__hint" data-qa="profile-points-remaining-text">
			{{ $t(remainingPoints ? 'myArea.headerInfo.pointsRemaining' : 'myArea.headerInfo.pointsTargetReached', { points: formattedRemainingPoints }) }}
		</p>
	</article>
</template>

<style lang="sass" scoped>
.profile-points-card
	display: flex
	flex-direction: column
	justify-content: center

	&__labels
		display: flex
		align-items: center
		justify-content: space-between
		gap: 1rem

		h2, p, strong
			margin: 0
			font-family: var(--lgei-headline)
			font-size: 0.75rem
			line-height: 1
			color: var(--lg-dark-gray-200)

	&__current
		h2
			font-size: 0.75rem
			font-weight: 600
			color: var(--club--title-default)

		p
			margin-top: 0.25rem

			strong
				font-size: 1.125rem
				font-weight: 700
				color: var(--lg-red-active)

	&__target
		text-align: right

	&__progress
		height: 0.5rem
		margin-top: 0.625rem
		overflow: hidden
		border-radius: 50rem
		background-color: var(--lg-mid-gray-100)

		span
			display: block
			height: 100%
			border-radius: inherit
			background-color: var(--lg-red-active)

	&__hint
		margin: 0.75rem 0 0
		font-family: var(--lgei-text)
		font-size: 0.75rem
		font-weight: 600
		line-height: 1.1
		color: var(--lg-mid-gray-200)
</style>
